import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { FmsService } from "src/app/services/fms.service";
import { environment } from "src/environments/environment";
import { HTTP_HEADER_CLIENT_ID } from "src/app/constants/technical.constants";

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private readonly fmsService = inject(FmsService);

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Check if the request URL matches the REST API root URL
    if (this.isRestApiRequest(req.url)) {
      const locale = this.fmsService.getLocale();

      // Add the Accept-Language header with the current locale
      const modifiedReq = req.clone({
        setHeaders: {
          "Accept-Language": locale,
          Language: locale,
          [HTTP_HEADER_CLIENT_ID.key]: HTTP_HEADER_CLIENT_ID.value,
        },
      });

      return next.handle(modifiedReq);
    }

    return next.handle(req);
  }

  /**
   * Checks if the request URL is for the REST API endpoint.
   *
   * Matches on the REST API path only (host-agnostic). The REST API root URL may be
   * absolute (e.g. https://api.storebrand.no/savings/pension/min-pensjon in prod) and the
   * test stage rewrites the host to api-t.storebrand.no (see ContractsService.buildRestApiRootUrl).
   * A host-based match would miss those rewritten URLs and drop the Language header, causing the
   * BFF to fall back to Norwegian for server-resolved strings even when the app is in English.
   */
  private isRestApiRequest(url: string): boolean {
    const restApiRootUrl = environment.restApiRootUrl;

    if (!restApiRootUrl) {
      return false;
    }

    // Strip any scheme + host so the comparison works regardless of the (possibly rewritten) host.
    const restApiPath = restApiRootUrl.replace(/^https?:\/\/[^/]+/, "");

    // Remove leading slash for a substring comparison that matches both relative and absolute URLs.
    const normalizedRestApiPath = restApiPath.startsWith("/") ? restApiPath.slice(1) : restApiPath;

    if (!normalizedRestApiPath) {
      return false;
    }

    return url.includes(normalizedRestApiPath);
  }
}
